import { api, apiVisitEngine } from '@/shared/api/client'
import type { IApiResponsePageable } from '@/shared/types/api'
import type {
  IVisitAdmitted,
  IVisitExaminationRequest,
  IVisitExaminationResponse,
  IVisitExaminationsResponse,
  IVisitMedicalDeferralRequest,
  IVisitsListParams,
} from '../model/visits.type'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/query-keys'
// ── Raw API ───────────────────────────────────────────────────────────────────

/**
 * Serialises the queue params: paging, a repeated `sort` entry per field, and
 * the server-side filters, which are left out entirely when empty so an unset
 * filter never narrows the result.
 */
const visitsListQuery = ({
  page,
  size,
  sort,
  search,
  date_from,
  date_to,
  ...multi
}: IVisitsListParams) => {
  const query = new URLSearchParams()
  query.set('page', String(page))
  query.set('size', String(size))
  sort.forEach((field) => query.append('sort', field))
  if (search?.trim()) query.set('search', search.trim())
  if (date_from) query.set('date_from', date_from)
  if (date_to) query.set('date_to', date_to)
  // Multi-select filters repeat the parameter once per picked value.
  // Unset filters arrive as `undefined` — Object.entries would hide that.
  const entries = Object.entries(multi) as Array<[string, string[] | undefined]>
  entries.forEach(([key, values]) => {
    for (const value of values ?? []) {
      if (value) query.append(key, value)
    }
  })
  return query.toString()
}

export const visitApi = {
  getVisitsAdmitted: async (params: IVisitsListParams) => {
    const response = await api.get<IApiResponsePageable<IVisitAdmitted>>(
      `/service/visit-engine/api/v1/visits/admitted?${visitsListQuery(params)}`,
    )
    return response.data.data
  },

  updateExamination: async (
    visitId: string,
    data: IVisitExaminationRequest,
  ) => {
    const response = await apiVisitEngine.post<IVisitExaminationResponse>(
      `/v1/visits/${visitId}/examination`,
      data,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  createMedicalDeferral: async (
    visitId: string,
    data: IVisitMedicalDeferralRequest,
  ) => {
    const response = await apiVisitEngine.post<IVisitExaminationResponse>(
      `/v1/visits/${visitId}/examination/medical-deferral`,
      data,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getExaminations: async (visitId: string) => {
    const response = await apiVisitEngine.get<IVisitExaminationsResponse>(
      `/v1/visits/${visitId}/examinations`,
    )
    return response.data.data
  },
}

export const useGetVisitsAdmittedQuery = (
  params: IVisitsListParams,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: [...queryKeys.visits.admitted(), params],
    queryFn: () => visitApi.getVisitsAdmitted(params),
    enabled,
  })

/**
 * Endless-scroll variant of the admitted queue, used by the donor pickers.
 * Callers that pass no `search` narrow the loaded rows themselves and keep
 * asking for pages while scrolling.
 */
export const useGetVisitsAdmittedInfiniteQuery = (size = 30, search?: string) =>
  useInfiniteQuery({
    queryKey: queryKeys.visits.admittedInfinite(size, search),
    queryFn: ({ pageParam }) =>
      visitApi.getVisitsAdmitted({ page: pageParam, size, sort: [], search }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined,
  })

export const useGetVisitExaminationsQuery = (visitId: string) =>
  useQuery({
    queryKey: queryKeys.visits.examinations(visitId),
    queryFn: () => visitApi.getExaminations(visitId),
    enabled: !!visitId,
  })

export const useUpdateVisitExaminationMutation = (
  visitId: string,
  donorId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: IVisitExaminationRequest) =>
      visitApi.updateExamination(visitId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.visits.examinations(visitId),
      })
    },
  })
}

export const useCreateMedicalDeferralMutation = (
  visitId: string,
  donorId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: IVisitMedicalDeferralRequest) =>
      visitApi.createMedicalDeferral(visitId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.visits.examinations(visitId),
      })
    },
  })
}
